<template>
    <div class="operator-app-section pt-2 pb-1" v-if="canView">
        <div class="d-flex align-center">
            <h2>{{ t("Operator application data") }}</h2>
            <v-btn class="ml-4" color="bal-dark-gray" dark small depressed @click="expanded = !expanded">
                <v-icon left small>{{ expanded ? "expand_less" : "expand_more" }}</v-icon>
                {{ expanded ? t("Hide") : t("Show") }}
                <span v-if="!expanded && summaryCount" class="ml-1">({{ summaryCount }})</span>
            </v-btn>
        </div>

        <div v-if="expanded" class="mt-3">
            <div v-if="!hasData" class="oa-empty">{{ t("No data from the operator application yet") }}</div>

            <template v-else>
                <!-- Pre-production checklist: one row per item, answer in the operator (yellow) cell -->
                <table v-if="checks.length" :style="[css.workOrderTable]" style="width: 100%" class="mb-4">
                    <tbody>
                        <tr :style="[css.tr]">
                            <td colspan="4" :style="[css.td, css.paddingBig, css.colorGray, css.textLeft]">
                                {{ t("Pre-production checklist") }}
                            </td>
                        </tr>
                        <tr v-for="item in checks" :key="'c' + item.id" :style="[css.tr]">
                            <td :style="[css.td, css.paddingSmall, css.colorGray, css.textLeft]">
                                {{ item.label || item.item_key }}
                            </td>
                            <td :style="[css.td, css.paddingSmall, css.colorYellow, css.width100]">
                                {{ item.checked ? t("Yes") : t("No") }}
                            </td>
                            <td :style="[css.td, css.paddingSmall, css.colorGray, css.width100]">{{ t("Note") }}</td>
                            <td :style="[css.td, css.paddingSmall, css.colorYellow, css.textLeft, css.width300]">
                                {{ item.note || "" }}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Monitoring rounds: one table per block, parameters as rows, rounds as columns -->
                <table
                    v-for="block in monitoringBlocks"
                    :key="'b' + block.index"
                    :style="[css.workOrderTable]"
                    style="width: 100%"
                    class="mb-4"
                >
                    <tbody>
                        <tr :style="[css.tr]">
                            <td
                                :colspan="block.rounds.length + 1"
                                :style="[css.td, css.paddingBig, css.colorGray, css.textLeft]"
                            >
                                {{ block.title || t("Monitoring") }}
                            </td>
                        </tr>
                        <tr :style="[css.tr]">
                            <td :style="[css.td, css.paddingSmall, css.colorGray, css.textLeft, css.paramCol]">
                                {{ t("Time") }}
                            </td>
                            <td
                                v-for="round in block.rounds"
                                :key="'h' + round.number"
                                :style="[css.td, css.paddingSmall, css.colorBlue]"
                            >
                                {{ round.time || "" }}
                            </td>
                        </tr>
                        <tr v-for="param in block.parameters" :key="'p' + param.name" :style="[css.tr]">
                            <td :style="[css.td, css.paddingSmall, css.colorGreen, css.textLeft, css.paramCol]">
                                {{ param.name }}<span v-if="param.unit"> ({{ param.unit }})</span>
                                <span v-if="param.expected" class="oa-target"> · {{ t("target") }} {{ param.expected }}</span>
                            </td>
                            <td
                                v-for="round in block.rounds"
                                :key="'v' + param.name + round.number"
                                :style="[css.td, css.paddingSmall, css.colorYellow]"
                            >
                                {{ param.values[round.number] || "" }}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Mixes: number row above the time row, like the shift report -->
                <table v-if="mixes.length" :style="[css.workOrderTable]" style="width: 100%" class="mb-4">
                    <tbody>
                        <tr :style="[css.tr]">
                            <td :colspan="mixes.length + 1" :style="[css.td, css.paddingBig, css.colorGray, css.textLeft]">
                                {{ t("Mixes") }} ({{ mixes.length }})
                            </td>
                        </tr>
                        <tr :style="[css.tr]">
                            <td :style="[css.td, css.paddingSmall, css.colorGray, css.textLeft, css.width100]">
                                {{ t("Mix") }}
                            </td>
                            <td v-for="mix in mixes" :key="'mn' + mix.id" :style="[css.td, css.paddingSmall, css.colorGray]">
                                {{ mix.sequence }}
                            </td>
                        </tr>
                        <tr :style="[css.tr]">
                            <td :style="[css.td, css.paddingSmall, css.colorGray, css.textLeft]">{{ t("Time") }}</td>
                            <td v-for="mix in mixes" :key="'mt' + mix.id" :style="[css.td, css.paddingSmall, css.colorYellow]">
                                {{ mix.mixed_time || "" }}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div class="oa-two">
                    <!-- Pallet log -->
                    <table v-if="pallets.length" :style="[css.workOrderTable]" style="width: 100%" class="mb-4">
                        <tbody>
                            <tr :style="[css.tr]">
                                <td colspan="3" :style="[css.td, css.paddingBig, css.colorGray, css.textLeft]">
                                    {{ t("Pallets") }} ({{ pallets.length }} · {{ palletBoxes }} {{ t("boxes") }})
                                </td>
                            </tr>
                            <tr :style="[css.tr]">
                                <td :style="[css.td, css.paddingSmall, css.colorGray]">{{ t("Pallet") }}</td>
                                <td :style="[css.td, css.paddingSmall, css.colorGray]">{{ t("Boxes") }}</td>
                                <td :style="[css.td, css.paddingSmall, css.colorGray]">{{ t("Registered") }}</td>
                            </tr>
                            <tr v-for="pallet in pallets" :key="'pl' + pallet.id" :style="[css.tr]">
                                <td :style="[css.td, css.paddingSmall, css.colorGray]">{{ pallet.sequence }}</td>
                                <td :style="[css.td, css.paddingSmall, css.colorYellow]">{{ pallet.boxes }}</td>
                                <td :style="[css.td, css.paddingSmall, css.colorBlue]">
                                    {{ formatDateTime(pallet.registered_at) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- Waste entries with the time they occurred -->
                    <table v-if="timedWastes.length" :style="[css.workOrderTable]" style="width: 100%" class="mb-4">
                        <tbody>
                            <tr :style="[css.tr]">
                                <td colspan="3" :style="[css.td, css.paddingBig, css.colorGray, css.textLeft]">
                                    {{ t("Waste entries") }} ({{ timedWastes.length }})
                                </td>
                            </tr>
                            <tr :style="[css.tr]">
                                <td :style="[css.td, css.paddingSmall, css.colorGray]">{{ t("Time") }}</td>
                                <td :style="[css.td, css.paddingSmall, css.colorGray]">{{ t("Type") }}</td>
                                <td :style="[css.td, css.paddingSmall, css.colorGray]">{{ t("Quantity (kg)") }}</td>
                            </tr>
                            <tr v-for="waste in timedWastes" :key="'w' + waste.id" :style="[css.tr]">
                                <td :style="[css.td, css.paddingSmall, css.colorBlue]">{{ waste.occurred_time }}</td>
                                <td :style="[css.td, css.paddingSmall, css.colorYellow]">{{ waste.reason }}</td>
                                <td :style="[css.td, css.paddingSmall, css.colorYellow]">{{ waste.quantity }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
/* Read-only view of what the operator application (tablet) wrote for this work order through
 * POST /api/v1/work-orders/import: the pre-production checklist, the monitoring rounds, the mix times,
 * the pallet log and the timed waste entries. These rows have no other place in the Tootja form.
 *
 * Reads activeWorkOrder.operator_app (checks, monitoring_values, mixes, pallets) and the wastes lists.
 * Visible to admin and superadmin only. No props, no events.
 *
 * Styled exactly like the work order table (work-order-view-table.vue): bordered Calibri table, the
 * same cell colours - gray = label, blue = system time, yellow = operator entry, green = monitoring
 * parameter (as in the printing view). The style object is a copy of that table's, kept in sync by hand. */
import { mapState } from "vuex";
import moment from "moment";

const VIEWER_ROLES = ["admin", "superadmin"];

export default {
    name: "work-order-view-operator-app",
    data() {
        return {
            expanded: false,
            css: {
                workOrderTable: {
                    borderCollapse: "collapse",
                    fontWeight: "bold",
                    textAlign: "center",
                    fontFamily: "Calibri, sans-serif",
                    fontSize: "12px",
                },
                tr: {
                    pageBreakInside: "avoid !important",
                },
                td: {
                    border: "1px solid black",
                    borderCollapse: "collapse",
                    padding: "2px",
                    fontSize: "12px",
                },
                paddingSmall: {
                    padding: "4px 6px",
                    height: "26px",
                },
                paddingBig: {
                    padding: "8px",
                    height: "35px",
                    fontSize: "14px",
                },
                colorGray: {
                    backgroundColor: "#d9d9d9",
                },
                colorBlue: {
                    backgroundColor: "#e0eaf6",
                },
                colorYellow: {
                    fontSize: "14px",
                    backgroundColor: "#fdf3d0",
                },
                colorGreen: {
                    backgroundColor: "#9fce63",
                },
                textLeft: {
                    textAlign: "left",
                },
                width100: {
                    width: "80px",
                },
                width300: {
                    width: "300px",
                },
                paramCol: {
                    width: "280px",
                },
            },
        };
    },
    computed: {
        ...mapState("WorkOrderStore", ["activeWorkOrder"]),
        ...mapState(["user"]),
        canView() {
            return !!this.user && VIEWER_ROLES.includes(this.user.role);
        },
        operatorApp() {
            return (this.activeWorkOrder && this.activeWorkOrder.operator_app) || {};
        },
        checks() {
            return this.operatorApp.checks || [];
        },
        mixes() {
            return (this.operatorApp.mixes || []).slice().sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        },
        pallets() {
            return (this.operatorApp.pallets || []).slice().sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        },
        palletBoxes() {
            return this.pallets.reduce((sum, p) => sum + (Number(p.boxes) || 0), 0);
        },
        /* Waste rows of both lines that carry the time the tablet stamps on every entry. */
        timedWastes() {
            const all = [].concat(this.activeWorkOrder.wastes || [], this.activeWorkOrder.wastes_secondary || []);
            return all.filter(w => w.occurred_time).sort((a, b) => (a.occurred_time > b.occurred_time ? 1 : -1));
        },
        /* monitoring_values -> blocks -> parameters x rounds, the shape the tablet's grid uses. */
        monitoringBlocks() {
            const blocks = {};
            (this.operatorApp.monitoring_values || []).forEach(row => {
                const key = row.block === null || row.block === undefined ? 0 : row.block;
                if (!blocks[key]) {
                    blocks[key] = { index: key, title: row.block_title || "", rounds: {}, params: {}, order: [] };
                }
                const block = blocks[key];
                const round = row.round_number === null || row.round_number === undefined ? 0 : row.round_number;
                if (!block.rounds[round]) block.rounds[round] = { number: round, time: row.measured_time || "" };
                if (!block.rounds[round].time && row.measured_time) block.rounds[round].time = row.measured_time;
                if (!block.params[row.parameter]) {
                    block.params[row.parameter] = {
                        name: row.parameter,
                        unit: row.unit || "",
                        expected: row.expected_value || "",
                        values: {},
                    };
                    block.order.push(row.parameter);
                }
                block.params[row.parameter].values[round] = row.value;
            });
            return Object.keys(blocks)
                .map(k => blocks[k])
                .sort((a, b) => a.index - b.index)
                .map(block => ({
                    index: block.index,
                    title: block.title,
                    rounds: Object.keys(block.rounds)
                        .map(r => block.rounds[r])
                        .sort((a, b) => a.number - b.number),
                    parameters: block.order.map(name => block.params[name]),
                }));
        },
        hasData() {
            return (
                this.checks.length > 0 ||
                this.mixes.length > 0 ||
                this.pallets.length > 0 ||
                this.timedWastes.length > 0 ||
                this.monitoringBlocks.length > 0
            );
        },
        summaryCount() {
            return (
                this.checks.filter(c => c.checked).length +
                this.mixes.length +
                this.pallets.length +
                this.timedWastes.length +
                (this.operatorApp.monitoring_values || []).length
            );
        },
    },
    methods: {
        formatDateTime(value) {
            return value ? moment(value, "YYYY-MM-DD HH:mm:ss").format("DD.MM.YYYY HH:mm") : "";
        },
    },
};
</script>

<style scoped lang="scss">
.operator-app-section {
    margin-top: 36px;
    border-top: solid 1px black;
}
.oa-empty {
    font-family: Calibri, sans-serif;
    font-size: 14px;
    padding: 4px 0;
}
.oa-target {
    font-weight: normal;
    color: #333;
}
.oa-two {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
    gap: 0 16px;
}
</style>
